import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Database, Users, Clock, Ban, Mail } from 'lucide-react';

const LOGO_URL = '/images/DraftFlow-billboard.png';

/**
 * Trust page (master spec Phase 7 item 1 / R9). Pre-login, plain language.
 *
 * This is NOT a replacement for the Privacy Policy at /privacy. That page is
 * the legal document and the one Google OAuth verification relies on. This page
 * is the version a teacher can read in a prep period and a parent or principal
 * can read without a lawyer, and it says so.
 *
 * Every claim here is a plain-language restatement of a commitment already
 * published in that policy. Nothing new is promised on the operator's behalf.
 */

const Section = ({ icon: Icon, title, children }) => (
  <section className="space-y-3">
    <h2 className="font-heading text-2xl font-bold text-white flex items-center gap-3">
      <Icon className="text-teal-flow" size={24} />
      {title}
    </h2>
    <div className="space-y-3 text-slate-300 leading-relaxed">{children}</div>
  </section>
);

const ROLE_ROWS = [
  {
    role: 'The student',
    sees: 'Their own drafts, their own coach conversations, and the feedback their teacher has released to them.',
    cannot: 'Any other student\'s work, drafts, or feedback.',
  },
  {
    role: 'Their teacher',
    sees: 'Drafts, revision history, coach conversations, and feedback for students in their own classes and on their own assignments.',
    cannot: 'Students in another teacher\'s classes.',
  },
  {
    role: 'Another teacher',
    sees: 'Nothing about students who are not in their classes.',
    cannot: 'Any part of another teacher\'s classroom.',
  },
  {
    role: 'The platform operator',
    sees: 'Production infrastructure, for keeping the service running and for handling deletion requests.',
    cannot: 'Nothing is shared with contractors or support staff. There are none.',
  },
];

const TrustPage = () => {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="px-6 py-4 border-b border-slate-800">
        <nav className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="DraftFlow" className="h-10" />
          </Link>
          <Link
            to="/"
            data-testid="trust-back-to-home"
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </nav>
      </header>

      {/* Content */}
      <main className="px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="card-surface p-8 md:p-12">
            <h1 className="font-heading text-4xl font-bold text-white mb-2">
              Student data, in plain language
            </h1>
            <p className="text-slate-400 mb-8">
              What DraftFlow stores, who can see it, and what we will never do with it.
            </p>
            <div className="text-sm text-slate-500 mb-10 pb-6 border-b border-slate-700">
              This page is a summary written for teachers, principals, and parents. The{' '}
              <Link to="/privacy" className="text-teal-flow hover:underline">Privacy Policy</Link>{' '}
              is the full legal document, and it governs if the two ever differ.
            </div>

            <div className="space-y-10">
              <Section icon={Ban} title="What we will never do">
                <ul className="space-y-2 list-disc list-inside">
                  <li><strong className="text-white">Student writing is never used to train AI models.</strong> Drafts are sent to Google's Gemini API to generate coaching feedback and, per Google's API terms, content sent this way is not used to train Google's general models.</li>
                  <li><strong className="text-white">Student data is never sold or rented.</strong></li>
                  <li><strong className="text-white">No targeted advertising, ever</strong>, and no commercial profiles built from student work.</li>
                  <li><strong className="text-white">The coach never writes for a student.</strong> It is built to ask questions and never to generate, write, or rewrite a student's text. The writing stays the student's own.</li>
                </ul>
              </Section>

              <Section icon={Database} title="What we store, and where">
                <p>
                  DraftFlow stores what the classroom needs and little else: a name and school email from Google sign-in, the classes and assignments a teacher creates, student drafts and their revision history, coach conversations, and AI or teacher feedback.
                </p>
                <p>All of it lives on United States infrastructure:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-700">
                        <th className="text-left py-2 pr-4 font-normal">What</th>
                        <th className="text-left py-2 pr-4 font-normal">Where</th>
                        <th className="text-left py-2 font-normal">Location</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-300">
                      <tr className="border-b border-slate-800"><td className="py-2 pr-4">The database</td><td className="py-2 pr-4">Supabase (PostgreSQL)</td><td className="py-2">United States</td></tr>
                      <tr className="border-b border-slate-800"><td className="py-2 pr-4">The application server</td><td className="py-2 pr-4">Google Cloud Run</td><td className="py-2">United States (us-central1)</td></tr>
                      <tr><td className="py-2 pr-4">The website</td><td className="py-2 pr-4">Netlify</td><td className="py-2">United States</td></tr>
                    </tbody>
                  </table>
                </div>
                <p>
                  Data is encrypted in transit (TLS 1.2 or higher) and at rest (AES-256). There are no passwords to steal: sign-in is handled entirely by Google.
                </p>
                <p>
                  When a draft goes to the AI for feedback, the student's name, email, and school name do not go with it. The AI sees the writing, the assignment prompt, and the standards being taught.
                </p>
              </Section>

              <Section icon={Users} title="Who can see what">
                <p>
                  Every table in the database enforces this at the database level, not just in the interface. A request for someone else's data returns nothing, even if someone goes looking for it.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-700">
                        <th className="text-left py-2 pr-4 font-normal">Role</th>
                        <th className="text-left py-2 pr-4 font-normal">Can see</th>
                        <th className="text-left py-2 font-normal">Cannot see</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-300">
                      {ROLE_ROWS.map((row) => (
                        <tr key={row.role} className="border-b border-slate-800 last:border-0 align-top">
                          <td className="py-3 pr-4 text-white whitespace-nowrap">{row.role}</td>
                          <td className="py-3 pr-4">{row.sees}</td>
                          <td className="py-3">{row.cannot}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>

              <Section icon={ShieldCheck} title="How this lines up with FERPA and COPPA">
                <p>
                  <strong className="text-white">FERPA.</strong> DraftFlow is built to be used as a school-authorized educational tool, the "school official" role FERPA describes. Student records are visible to the student and their own teachers, and to no other party inside the platform.
                </p>
                <p>
                  <strong className="text-white">COPPA.</strong> DraftFlow is intended for high school students, typically ages 14 to 18, working under their teacher's supervision. Students cannot create an account on their own: they get in through a teacher's class code or assignment link. Consent flows through the teacher and school, which is the school-authorized-service path COPPA provides for.
                </p>
                <p>
                  Parents and eligible students may ask to review or delete a student's information. The contact address is at the bottom of this page.
                </p>
              </Section>

              <Section icon={Clock} title="How long it is kept">
                <p>
                  Data is kept while the account is active and the class relationship is current. Anyone, including a parent or a school administrator, can request deletion at any time, and that removes the profile, drafts, feedback history, peer reviews, and associated records.
                </p>
                <p className="text-slate-400">
                  Being straight about the current state: deletion requests are handled by hand today, by the operator. Automated export and deletion tooling is in progress, and this page will say so when it ships.
                </p>
              </Section>

              <Section icon={Mail} title="Who to ask">
                <p>
                  DraftFlow is built and operated by Jimmy Norman, a literacy specialist and instructional coach, as part of Classroom Ready AI. Questions about student data, deletion requests, or a district review go to{' '}
                  <a href="mailto:contact@draftflow.pro" className="text-teal-flow hover:underline">contact@draftflow.pro</a>.
                </p>
                <p>
                  For the complete legal terms, read the{' '}
                  <Link to="/privacy" className="text-teal-flow hover:underline">Privacy Policy</Link> and the{' '}
                  <Link to="/terms" className="text-teal-flow hover:underline">Terms of Service</Link>.
                </p>
              </Section>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 py-8 border-t border-slate-800">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-slate-500 text-sm">&copy; 2026 DraftFlow. All rights reserved.</span>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/" className="text-slate-400 hover:text-white transition-colors">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TrustPage;
